import React from 'react';
import getConfig from 'next/config';

import SkipLink from '@domain-group/fe-co-skip-link';
import Header from '@domain-group/fe-co-header';

import { ErrorBoundary, SearchResults, SearchResults2026 } from '..';
import { mapHeaderData } from '../header/utils';
import { skipLinkName } from '../../helper/accessibility-utils';
import { UserDetailsRedacted } from '../../@types';
import type { AppPageProps } from '../../pages/_app';

const { publicRuntimeConfig } = getConfig();

export interface SearchResultsPageProps extends AppPageProps {
  isEmbeddedApp: boolean;
  user?: UserDetailsRedacted;
  suburbs: string;
  sortBy?: string;
  page?: string;
  keywordName?: string;
  agentFilter?: string;
  mixpanelHasInitialized?: boolean;
  isNewSearchPage?: boolean;
  isAgency: boolean;
}

export type SearchResultsServerSideProps = Omit<
  SearchResultsPageProps,
  'isAgency'
>;

/**
 * Shared shell for the agent and agency search results pages. The only
 * difference between the two is `isAgency`, so both page routes render this.
 */
export const SearchResultsPage = ({
  isEmbeddedApp = false,
  user,
  suburbs,
  sortBy,
  keywordName,
  agentFilter,
  page = '1',
  mixpanelHasInitialized = false,
  isNewSearchPage = false,
  isAgency,
}: SearchResultsPageProps): JSX.Element => {
  const Results = isNewSearchPage ? SearchResults2026 : SearchResults;

  return (
    <ErrorBoundary>
      <>
        <SkipLink anchorName={skipLinkName} />
        {isEmbeddedApp ? null : (
          <div data-theme-name="domain-2022">
            <Header
              {...mapHeaderData({
                baseUrl: publicRuntimeConfig.BASE_URL,
                breadcrumbsData: null,
              })}
            />
          </div>
        )}
        <Results
          isEmbeddedApp={isEmbeddedApp}
          isAgency={isAgency}
          suburbs={suburbs}
          sortBy={sortBy}
          page={page}
          keywordName={keywordName}
          agentFilter={agentFilter}
          skipLinkContentId={skipLinkName}
          user={user}
          mixpanelHasInitialized={mixpanelHasInitialized}
        />
      </>
    </ErrorBoundary>
  );
};

export const createSearchResultsPage = ({
  isAgency,
}: {
  isAgency: boolean;
}): ((props: SearchResultsServerSideProps) => JSX.Element) => {
  const Page = (props: SearchResultsServerSideProps): JSX.Element => (
    <SearchResultsPage {...props} isAgency={isAgency} />
  );

  Page.displayName = isAgency ? 'AgencySearchResults' : 'AgentSearchResults';

  return Page;
};

export default SearchResultsPage;
